/* GAINR V2: the click never depends on the optional email form. */
(() => {
  'use strict';
  const dialog = document.getElementById('GainrInterestDialog');
  let opener;
  const measurementId = (document.body.dataset.ga4Id || '').trim().toUpperCase();
  const hasGA4 = /^G-[A-Z0-9]+$/.test(measurementId);
  const isEditor = () => Boolean(window.Shopify && window.Shopify.designMode);
  const gaQueue = [];
  const shopifyQueue = [];
  let gaStarted = false;
  let privacyRequested = false;
  let attempts = 0;
  let timer;

  // Retain only campaign parameters. Email form values never enter analytics.
  const pageLocation = new URL(window.location.href);
  const campaign = new URLSearchParams();
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
    if (pageLocation.searchParams.has(key)) campaign.set(key, pageLocation.searchParams.get(key));
  });
  pageLocation.search = campaign.toString();
  pageLocation.hash = '';
  let referrer = '';
  try { referrer = new URL(document.referrer).origin; } catch (_) { /* Direct visit. */ }
  const pageData = { page_location: pageLocation.href, page_referrer: referrer, page_title: document.title };

  function privacyAPI() { return window.Shopify && window.Shopify.customerPrivacy; }
  function analyticsAllowed() {
    try { return privacyAPI()?.analyticsProcessingAllowed() === true; } catch (_) { return false; }
  }
  function startGA4() {
    if (!hasGA4 || isEditor() || !analyticsAllowed()) return false;
    window['ga-disable-' + measurementId] = false;
    if (gaStarted) return true;
    gaStarted = true;
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.gtag('consent', 'default', {
      analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied'
    });
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      ...pageData, send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false
    });
    window.gtag('event', 'page_view', { ...pageData, send_to: measurementId });
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(measurementId);
    document.head.appendChild(script);
    return true;
  }
  function sendGA4(data) {
    if (startGA4()) window.gtag('event', 'gainr_interest_click', { ...pageData, ...data, send_to: measurementId });
  }
  function syncConsent() {
    if (!hasGA4 || isEditor()) return;
    if (!analyticsAllowed()) {
      gaQueue.length = 0;
      if (gaStarted) {
        window['ga-disable-' + measurementId] = true;
        window.gtag('consent', 'update', { analytics_storage: 'denied' });
      }
      return;
    }
    if (gaStarted) {
      window['ga-disable-' + measurementId] = false;
      window.gtag('consent', 'update', { analytics_storage: 'granted' });
    }
    startGA4();
    gaQueue.splice(0).forEach(sendGA4);
  }
  function flushNativeEvents() {
    if (typeof window.Shopify?.analytics?.publish !== 'function') return;
    shopifyQueue.splice(0).forEach((data) => {
      try {
        // Publishing is an event bus, NOT a persistent counter in Shopify reports.
        Promise.resolve(window.Shopify.analytics.publish('gainr_interest_click', data)).catch(() => {});
      } catch (_) { /* Confirmation and GA4 remain independent of the native bus. */ }
    });
  }
  function prepareTracking() {
    if (isEditor()) return;
    flushNativeEvents();
    if (hasGA4) {
      if (privacyAPI()) syncConsent();
      else if (!privacyRequested && typeof window.Shopify?.loadFeatures === 'function') {
        privacyRequested = true;
        try {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
            if (!error) syncConsent();
            else gaQueue.length = 0;
          });
        } catch (_) { gaQueue.length = 0; }
      }
    }
    // Bounded readiness check for visitors who click before Shopify's scripts finish.
    if (attempts++ < 25 && ((!privacyAPI() && hasGA4) || shopifyQueue.length)) {
      timer = window.setTimeout(prepareTracking, 200);
    } else if (attempts >= 25) { gaQueue.length = 0; shopifyQueue.length = 0; }
  }
  function recordInterest(source) {
    if (isEditor()) return;
    const data = { cta_source: source, page_path: window.location.pathname };
    shopifyQueue.push(data);
    flushNativeEvents();
    if (hasGA4) {
      if (privacyAPI()) sendGA4(data);
      else gaQueue.push(data);
    }
    window.clearTimeout(timer);
    attempts = 0;
    prepareTracking();
  }
  function openDialog(trigger) {
    if (!dialog) return;
    opener = trigger;
    if (!dialog.open) dialog.showModal();
    document.body.classList.add('interest-dialog-open');
    dialog.querySelector('.form-success, .form-error, #GainrInterestTitle')?.focus();
  }
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-interest-source]');
    const link = event.target.closest('a');
    const menu = (trigger || link)?.closest('.mobile-nav');
    if (menu) menu.open = false;
    if (trigger) {
      if (!trigger.hasAttribute('data-product-link')) event.preventDefault();
      recordInterest(trigger.dataset.interestSource);
      if (!trigger.hasAttribute('data-product-link')) openDialog(trigger);
    }
    if (event.target.closest('[data-interest-close]')) dialog?.close();
    if (event.target === dialog) {
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
    }
  });
  dialog?.addEventListener('close', () => {
    document.body.classList.remove('interest-dialog-open');
    // The menu button is hidden once its details element closes.
    const target = opener?.closest('.mobile-nav')?.querySelector('summary') || opener;
    target?.focus();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') document.querySelectorAll('.mobile-nav[open]').forEach((menu) => {
      menu.open = false;
      menu.querySelector('summary').focus();
    });
  });
  document.addEventListener('visitorConsentCollected', syncConsent);
  // Returning from a Shopify email submission must not count another interest click.
  if (dialog?.querySelector('.form-success, .form-error')) openDialog(null);
  document.querySelectorAll('.optional-email').forEach((details) => {
    if (details.querySelector('.form-success, .form-error')) details.open = true;
  });
  prepareTracking();
})();
